import { useState } from "react";
import TypeWriterText from "../../components/TypeWriterText";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight, MapPin, Award, Shield, Heart, CheckCircle } from "lucide-react";
import KennelSEO, { localBusinessSchema, faqSchema } from "./KennelSEO";

const BASE = "https://wolfevomkatzenberg.pl";

const faqData = [
  { q: "Ile kosztuje szczenie owczarka niemieckiego z hodowli?", a: "Cena szczeniecia z profesjonalnej hodowli zarejestrowanej w ZKwP i FCI zalezy od wielu czynnikow — rodowodu rodzicow, ich osiagniec, badan zdrowotnych i wkladu hodowcy w socjalizacje. Warto inwestowac w szczenie z pewnego zrodla, co daje gwarancje zdrowia i stabilnej psychiki." },
  { q: "Czy hodowla Wolfe vom Katzenberg ma aktualne mioty?", a: "Informacje o aktualnych i planowanych miotach sa dostepne na naszej stronie. Zapraszamy do kontaktu w celu rezerwacji lub zapytania o dostepnosc szczeniat." },
  { q: "Skad pochodzi hodowla Wolfe vom Katzenberg?", a: "Hodowla Wolfe vom Katzenberg jest zarejestrowana w Polsce, w Zwiazku Kynologicznym w Polsce (ZKwP) oraz Miedzynarodowej Federacji Kynologicznej (FCI)." },
  { q: "Czy szczenieta maja rodowod FCI?", a: "Tak. Wszystkie nasze szczenieta otrzymuja pelna dokumentacje rodowodowa potwierdzona przez ZKwP i FCI, wraz z ksiazeczka zdrowia, chipem i szczepieniami." },
  { q: "Na co zwrocic uwage kupujac szczenie owczarka niemieckiego?", a: "Przede wszystkim na badania zdrowotne rodzicow (HD, ED, DNA), rodowod, warunki chowu, socjalizacje szczeniat oraz podejscie hodowcy do charakteru i uzytkowosci rasy." },
  { q: "Czy owczarek niemiecki jest dobrym psem rodzinnym?", a: "Tak, o ile pochodzi z odpowiedzialnej hodowli i jest prawidlowo prowadzony. Owczarek niemiecki jest lojalny, komunikatywny i silnie zwiazany z rodzina. Wymaga konsekwencji i aktywnosci." },
  { q: "Jakie badania maja psy hodowlane w Wolfe vom Katzenberg?", a: "Nasze psy posiadaja kompletne badania zdrowotne: HD (stawy biodrowe), ED (stawy lokciowe), profil DNA, testy psychiczne oraz egzaminy uzytkowe (BH, IGP, Korung)." },
  { q: "Czy moge zarezerwowac szczenie przed urodzeniem miotu?", a: "Tak, przyjmujemy rezerwacje na planowane mioty. Zapraszamy do kontaktu, aby omowic szczegoly i poznac nasz proces rezerwacji." },
];

const benefits = [
  { icon: Award, title: "Rodowod FCI/ZKwP", text: "Kazde szczenie z pelna dokumentacja rodowodowa" },
  { icon: Shield, title: "Badania HD/ED/DNA", text: "Rodzice z kompletnymi badaniami zdrowotnymi" },
  { icon: Heart, title: "Socjalizacja", text: "Szczenieta socjalizowane od pierwszych dni zycia" },
  { icon: CheckCircle, title: "Wsparcie hodowcy", text: "Staly kontakt i pomoc po adopcji szczeniecia" },
];

const FAQItem = ({ q, a, isOpen, onToggle, idx }) => (
  <div className="border border-[#2A2A2D] hover:border-[#D4AF37]/20 transition-colors" data-testid={`puppy-faq-${idx}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between p-6 text-left group">
      <span className="text-sm md:text-base text-[#F5F5F5] pr-4 group-hover:text-[#D4AF37] transition-colors">{q}</span>
      <ChevronDown className={`w-4 h-4 text-[#D4AF37] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
    </button>
    <motion.div initial={false} animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
      <p className="px-6 pb-6 text-sm text-[#B8B8B8]/70 leading-relaxed">{a}</p>
    </motion.div>
  </div>
);

const KennelPuppySEO = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="bg-[#0B0B0D] min-h-screen">
      <KennelSEO
        title="Szczenieta owczarka niemieckiego na sprzedaz | Wolfe vom Katzenberg"
        description="Szukasz szczeniecia owczarka niemieckiego z profesjonalnej hodowli? Wolfe vom Katzenberg — szczenieta z rodowodem FCI, badania HD/ED, socjalizacja. Hodowla owczarkow niemieckich w Polsce."
        canonical={`${BASE}/szczenieta-owczarka-niemieckiego`}
        ogImage={`${BASE}/dog_profile.png`}
        schema={[localBusinessSchema, faqSchema(faqData)]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#141416] border-b border-[#2A2A2D]">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #D4AF37 0px, #D4AF37 1px, transparent 1px, transparent 40px)" }} />
        <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-[#D4AF37]/[0.04] rounded-full blur-[100px] -translate-y-1/2 translate-x-1/4" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-6">HODOWLA OWCZARKA NIEMIECKIEGO</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-serif text-[clamp(1.8rem,4.5vw,3.2rem)] text-[#F5F5F5] leading-tight mb-8" data-testid="puppy-seo-title"
          >
            Szczenieta owczarka niemieckiego{" "}
            <span className="text-[#D4AF37]">z profesjonalnej hodowli</span>
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="w-16 h-px bg-[#D4AF37]/40 mb-8" />
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="text-[#B8B8B8] text-sm md:text-base leading-relaxed max-w-3xl"
          >
            Hodowla Wolfe vom Katzenberg to miejsce, w ktorym kazde szczenie owczarka niemieckiego rodzi sie w przemyslany sposob — z dbalocia o zdrowie, charakter i przyszlosc rasy. Nasze psy posiadaja pelne badania zdrowotne, rodowody FCI i doswiadczenie szkoleniowe.
          </motion.p>
        </div>
      </section>

      {/* Benefits */}
      <section className="px-6 md:px-12 py-20 md:py-24">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-14">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">DLACZEGO MY</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">Co wyroznia nasze szczenieta</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30" />
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map(({ icon: Icon, title, text }, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.05 }}
                className="relative bg-gradient-to-br from-[#1A1A1D] to-[#111113] border border-[#2A2A2D]/60 p-6 group overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-500 text-center"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-sm bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 flex items-center justify-center group-hover:bg-[#D4AF37]/[0.15] group-hover:border-[#D4AF37]/40 group-hover:shadow-[0_0_20px_rgba(212,175,55,0.1)] transition-all duration-500">
                    <Icon className="w-5 h-5 text-[#D4AF37]/70 group-hover:text-[#D4AF37] transition-colors duration-500" />
                  </div>
                  <h3 className="text-[#F5F5F5] text-sm font-medium mb-1.5 group-hover:text-[#D4AF37] transition-colors duration-300">{title}</h3>
                  <p className="text-[#B8B8B8]/50 text-xs leading-relaxed">{text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About breeding */}
      <section className="bg-[#141416] px-6 md:px-12 py-20 md:py-24 border-y border-[#2A2A2D]">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">O HODOWLI</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">Hodowla owczarka niemieckiego Wolfe vom Katzenberg</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-8" />
            <div className="space-y-5 text-sm text-[#B8B8B8] leading-relaxed">
              <p>Hodowla Wolfe vom Katzenberg jest zarejestrowana w Zwiazku Kynologicznym w Polsce (ZKwP) oraz Miedzynarodowej Federacji Kynologicznej (FCI). Nasze psy hodowlane posiadaja pelne badania zdrowotne — HD, ED, profil DNA — a takze doswiadczenie wystawowe i szkoleniowe.</p>
              <p>Kazdy miot jest starannie planowany z uwzglednieniem zdrowia, charakteru i uzytkowosci. Szczenieta sa socjalizowane od pierwszych dni zycia, maja kontakt z czlowiekiem i roznorodnymi bodzcami, co buduje stabilna psychike i pewnosc siebie.</p>
              <p>Jako hodowcy zapewniamy pelna dokumentacje rodowodowa, ksiazeczke zdrowia, chip, szczepienia oraz staly kontakt i wsparcie po adopcji szczeniecia.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to buy */}
      <section className="px-6 md:px-12 py-20 md:py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">PROCES</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">Jak kupic szczenie owczarka niemieckiego</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-8" />
            <div className="space-y-5 text-sm text-[#B8B8B8] leading-relaxed">
              <p>Proces adopcji szczeniecia z naszej hodowli zaczyna sie od kontaktu — chetnie odpowiemy na pytania dotyczace rasy, naszych psow hodowlanych, aktualnych i planowanych miotow.</p>
              <p>Po wstepnej rozmowie zapraszamy do odwiedzenia hodowli, poznania naszych psow i zobaczenia warunkow, w jakich rosna szczenieta. Rezerwacja odbywa sie na podstawie indywidualnej rozmowy — chcemy miec pewnosc, ze kazde szczenie trafia do swiadomego i odpowiedzialnego wlasciciela.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#141416] px-6 md:px-12 py-20 md:py-24 border-y border-[#2A2A2D]">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">FAQ</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">Najczesciej zadawane pytania o szczenieta</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30" />
          </motion.div>
          <div className="space-y-3" data-testid="puppy-faq-accordion">
            {faqData.map((item, idx) => (
              <FAQItem key={idx} idx={idx} q={item.q} a={item.a} isOpen={openFaq === idx} onToggle={() => setOpenFaq(openFaq === idx ? null : idx)} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 py-24 md:py-28">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <TypeWriterText />
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2rem)] text-[#F5F5F5] mb-6">Zainteresowany szczenieciem owczarka niemieckiego?</h2>
            <p className="text-[#B8B8B8] text-sm leading-relaxed mb-10 max-w-2xl mx-auto">
              Skontaktuj sie z nami, aby dowiedziec sie wiecej o dostepnych i planowanych miotach. Chetnie odpowiemy na wszystkie pytania.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/kontakt" data-testid="puppy-cta-contact"
                className="px-10 py-3.5 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.2em] hover:bg-[#C8A96A] transition-all duration-300 hover:shadow-lg hover:shadow-[#D4AF37]/20 flex items-center gap-2"
              >ZAPYTAJ O SZCZENIETA <ArrowRight className="w-3.5 h-3.5" /></Link>
              <Link to="/psy" data-testid="puppy-cta-dogs"
                className="px-10 py-3.5 border border-[#D4AF37] text-[#D4AF37] text-xs tracking-[0.2em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300 flex items-center gap-2"
              >POZNAJ NASZE PSY <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default KennelPuppySEO;
